import { AnalysisService } from "../../services/analysis/AnalysisService";
import { ReviewedDetectionDto } from "../../services/ocr/InspectionReviewService";
import { inspectionInfoStorage } from "../../services/ocr/InspectionInfoStorageModel";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";

/**
 * Which set of detections the Video Analysis tab is showing.
 *
 * A video-analysis run has no ocr.json — its detections come straight out of the analysis
 * output. The moment a reviewer edits, adds or approves something, those corrections are
 * kept separately (the review record) so the raw model output is never overwritten. This
 * type is what lets the user say which of the two they want to look at.
 */
export type DetectionSource = "approved" | "original" | "compare";

/** Where a row in the approved list came from, relative to the raw analysis output. */
export type DetectionProvenance = "AI" | "Edited" | "Added";

// Distance tolerance for matching an AI detection to an inspector (ground-truth)
// detection of the same main code.
export const GT_MATCH_TOLERANCE = 0.3;

export interface AnalysisDetection {
  id: string;
  mainCode: string;
  c1: string;
  c2: string;
  accuracy: number;
  meters: number;
  timestamp: number;
  beginDistance?: number;
  endDistance?: number;
  /**
   * The rest of the coded observation. Present only on rows that come from the REVIEW: the
   * analysis output has a code and a position and nothing else, while an approved row carries
   * whatever the reviewer filled in — which is what the approved graph has to show to be the
   * same table the Inspection Insights side draws.
   */
  sa?: string;
  q1?: string;
  q2?: string;
  positionFrom?: string;
  positionTo?: string;
  text?: string;
  /** Only set on rows that come from the review (the approved list). */
  provenance?: DetectionProvenance;
  /** Index into the review's detection array, for rows that come from the review. */
  reviewIndex?: number;
}

/**
 * The analysis run's time -> metre track: the explicit `Distances` table from -analysis.json, or,
 * when a run did not emit one, a track derived from every RoI bucket that carries a distance.
 * Empty when the file has neither (the distance ROI was not labelled for that run).
 */
export function distanceTrack(videoAnalysis: AnalysisService): { time: number; meters: number }[] {
  const distances = videoAnalysis.vaData?.distances ?? [];
  if (distances.length) return distances;

  const roisPerAccuracy = videoAnalysis.vaData?.roisPerAccuracy ?? {};
  const roiTrack: { time: number; meters: number }[] = [];
  for (const key in roisPerAccuracy) {
    for (const r of (roisPerAccuracy as any)[key] ?? []) {
      if (typeof r?.time === "number" && typeof r?.distance === "number" && r.distance > 0) {
        roiTrack.push({ time: r.time, meters: r.distance });
      }
    }
  }
  return roiTrack.sort((a, b) => a.time - b.time);
}

/**
 * The station at a video position: the last track entry at or before `time` (the counter shows
 * the last reading until the next one). Undefined when the track is empty or starts later.
 */
export function distanceAtTime(
  track: readonly { time: number; meters: number }[],
  time: number
): number | undefined {
  let meters: number | undefined;
  let best = -Infinity;
  for (const d of track) {
    if (d.time <= time && d.time > best) {
      best = d.time;
      meters = d.meters;
    }
  }
  return meters;
}

/**
 * The station at a video position, INTERPOLATED between the readings either side of it.
 *
 * The counter is not read continuously: a run can go half a minute without a legible reading
 * while the camera keeps moving (1224_4480 has nothing between 479 s at 17.18 m and 511.5 s at
 * 20.70 m). The last-reading rule of distanceAtTime then answers 17.18 for 8:31, half a second
 * before the counter says 20.70. Interpolating gives 20.64, which is where the camera was.
 *
 * For PRE-FILLING a station only. The analysis rows keep distanceAtTime: their distance is part
 * of their identity, and moving it would detach every approved row from its machine row.
 * Before the first reading: undefined. After the last: the last reading.
 */
export function interpolatedDistanceAtTime(
  track: readonly { time: number; meters: number }[],
  time: number
): number | undefined {
  let before: { time: number; meters: number } | undefined;
  let after: { time: number; meters: number } | undefined;
  for (const d of track) {
    if (d.time <= time && (!before || d.time > before.time)) before = d;
    if (d.time > time && (!after || d.time < after.time)) after = d;
  }

  if (!before) return undefined;
  if (!after || after.time === before.time) return before.meters;

  const fraction = (time - before.time) / (after.time - before.time);
  return before.meters + fraction * (after.meters - before.meters);
}

/**
 * The detections as the model produced them, at the confidence currently selected.
 *
 * Extracted from AnalysisInspectionGraph so the graph and the detection list are built from
 * one function — they used to derive their rows independently, which is how the list could
 * sit empty while the graph clearly had detections.
 */
export function buildAnalysisDetections(videoAnalysis: AnalysisService): AnalysisDetection[] {

  // Per-timestamp predictions collapsed to one row per (code, second), keeping the peak
  // accuracy. Used directly when the analysis has no RoI data, AND as the fallback when RoI
  // data IS present but yields nothing at the current confidence. Some analysis files carry
  // RoIs only at higher thresholds, so the default Overall 0.65 finds no RoIs and the table
  // showed "No rows" even though the plot (fed by the same per-timestamp predictions) clearly
  // had detections. Falling back keeps the table/pipe in sync with the plot.
  const buildFromPredictions = (): AnalysisDetection[] => {
    // Distance track for positioning prediction rows along the pipe. Prefer the explicit
    // Distances table; if the analysis didn't emit one (some files carry distance ONLY inside
    // their RoI regions), derive a time->metre track from EVERY RoI bucket (any confidence,
    // any code) so rows still show a distance instead of "—". If neither the Distances table
    // nor any RoI carries distance, the file genuinely has none (the distance ROI wasn't
    // labelled for that run) and the column stays "—".
    const track = distanceTrack(videoAnalysis);
    const distanceAt = (time: number): number => distanceAtTime(track, time) ?? 0;

    const byKey = new Map<string, AnalysisDetection>();
    videoAnalysis.allPredictions
      .filter((p) => p?.predictionDetails?.mainCode)
      .forEach((p) => {
        const det: AnalysisDetection = {
          id: `${p.predictionDetails.mainCode}@${Math.round(p.timestamp)}`,
          mainCode: p.predictionDetails.mainCode,
          c1: p.predictionDetails.c1Code || "",
          c2: p.predictionDetails.c2Code || "",
          accuracy: p.predictionDetails.accuracy ?? 0,
          meters: distanceAt(p.timestamp),
          timestamp: p.timestamp,
        };
        const existing = byKey.get(det.id);
        if (!existing || det.accuracy > existing.accuracy) byKey.set(det.id, det);
      });
    return Array.from(byKey.values());
  };

  // Prefer region-level RoIs when the analysis provides them: one row per detection region,
  // each carrying its own peak distance.
  if (videoAnalysis.hasRoiData) {
    const list: AnalysisDetection[] = videoAnalysis.roiDetections.map((r, i) => {
      // RoIs carry no C1/C2; look them up at the peak time/main code (as the demo does) so
      // "Show Characterizations" has something to append.
      const chars = (videoAnalysis as any).characterizationAtTime(r.time)?.[r.mainCode];
      return {
        id: `${r.mainCode}@${r.time.toFixed(2)}@${i}`,
        mainCode: r.mainCode,
        c1: chars?.C1 ?? "",
        c2: chars?.C2 ?? "",
        accuracy: r.accuracy,
        meters: r.meters,
        timestamp: r.time,
        beginDistance: r.beginDistance,
        endDistance: r.endDistance,
      };
    });

    // In Overall mode, always surface a ground-truth code's matching AI detection even below
    // the threshold (same rule as the Comparison tab): match the AI RoIs against the inspector
    // (OCR) detections and pull in the best one when the active threshold hid it.
    const gt = inspectionInfoStorage.detections;
    if (videoAnalysis.analysisConfidenceOption === ConfidenceOptions.Overall && gt.length) {
      const allRois = (videoAnalysis as any).roiDetectionsAtMinConfidence as Array<{
        mainCode: string;
        time: number;
        meters: number;
        beginDistance: number;
        endDistance: number;
        accuracy: number;
      }>;
      gt.forEach((g: any, gi: number) => {
        const gDist = Number(g.distance ?? 0);
        const alreadyShown = list.some(
          (m) => m.mainCode === g.main_Code && Math.abs(m.meters - gDist) <= GT_MATCH_TOLERANCE
        );
        if (alreadyShown) return;
        const candidates = allRois.filter(
          (r) => r.mainCode === g.main_Code && Math.abs(r.meters - gDist) <= GT_MATCH_TOLERANCE
        );
        if (!candidates.length) return;
        const best = candidates.reduce((a, b) => (b.accuracy > a.accuracy ? b : a));
        if (list.some((m) => m.mainCode === best.mainCode && Math.abs(m.meters - best.meters) < 0.001))
          return;
        const chars = (videoAnalysis as any).characterizationAtTime(best.time)?.[best.mainCode];
        list.push({
          id: `${best.mainCode}@${best.time.toFixed(2)}@gt${gi}`,
          mainCode: best.mainCode,
          c1: chars?.C1 ?? "",
          c2: chars?.C2 ?? "",
          accuracy: best.accuracy,
          meters: best.meters,
          timestamp: best.time,
          beginDistance: best.beginDistance,
          endDistance: best.endDistance,
        });
      });
    }
    // RoIs are present but none matched the selected confidence (e.g. an analysis whose RoIs
    // sit only at higher thresholds, so the default Overall 0.65 finds none). Fall back to the
    // prediction-based rows so the table/pipe match the plot instead of showing "No rows".
    if (list.length === 0) return buildFromPredictions();
    return list;
  }

  return buildFromPredictions();
}

/** The raw analysis output in the shape the review stores, ready to seed a new review. */
export function analysisToReviewedDetections(
  detections: AnalysisDetection[]
): ReviewedDetectionDto[] {
  return detections
    .map((d) => ({
      side: "Analysis" as const,
      source: "AI",
      decision: "Accepted",
      confidence: d.accuracy,
      mainCode: d.mainCode,
      distance: d.meters,
      timeSeconds: d.timestamp,
      c1: d.c1 ?? "",
      c2: d.c2 ?? "",
    }))
    .sort((a, b) => a.distance - b.distance) as ReviewedDetectionDto[];
}

/**
 * OCR-shaped detections (ocr.json rows, or the OCR-side approved list) in the shape the graph
 * and the side-by-side comparison draw. OCR timestamps are clock strings, so they convert to
 * seconds the same way the rest of the inspection UI does.
 */
export function ocrToAnalysisDetections(
  detections: Array<Record<string, any>>,
  toSeconds: (timestamp?: string) => number
): AnalysisDetection[] {
  return detections.map((d, i) => ({
    id: `ocr-${i}`,
    mainCode: d.main_Code ?? "",
    c1: d.c1 ?? "",
    c2: d.c2 ?? "",
    accuracy: Number(d.confidence ?? 0),
    meters: Number(d.distance ?? 0),
    timestamp: toSeconds(d.timestamp),
  }));
}

/** Every row marked Approved, for the one-click bulk sign-off. */
export function approveAll(detections: ReviewedDetectionDto[]): ReviewedDetectionDto[] {
  return detections.map((d) => ({ ...d, detectionStatus: "Approved" as const }));
}

/** An untouched AI proposal, a corrected one, or a row the reviewer created. */
export function provenanceOf(d: ReviewedDetectionDto): DetectionProvenance {
  if (d.decision === "Added" || d.source === "User") return "Added";
  if (d.decision === "Edited") return "Edited";
  return "AI";
}

/** The review's detections in the shape the graph draws. */
export function reviewedToAnalysisDetections(
  detections: ReviewedDetectionDto[]
): AnalysisDetection[] {
  return detections.map((d, i) => ({
    id: `${d.stableKey ?? `review-${i}`}`,
    mainCode: d.mainCode ?? "",
    c1: d.c1 ?? "",
    c2: d.c2 ?? "",
    sa: d.sa ?? "",
    q1: d.q1 ?? "",
    q2: d.q2 ?? "",
    positionFrom: d.positionFrom ?? "",
    positionTo: d.positionTo ?? "",
    text: d.note ?? "",
    accuracy: d.confidence ?? 0,
    meters: d.distance ?? 0,
    timestamp: d.timeSeconds ?? 0,
    provenance: provenanceOf(d),
    reviewIndex: i,
  }));
}

/**
 * The rows to show for the chosen source.
 *
 * "approved" falls back to the raw analysis output whenever the review has nothing yet, so a
 * freshly finished run shows its detections immediately instead of an empty list behind an
 * import button.
 */
export function resolveDetections(
  source: DetectionSource,
  videoAnalysis: AnalysisService,
  reviewDetections: ReviewedDetectionDto[]
): { rows: AnalysisDetection[]; isFallback: boolean } {
  const original = buildAnalysisDetections(videoAnalysis);
  if (source === "original") return { rows: original, isFallback: false };
  if (!reviewDetections.length) return { rows: original, isFallback: true };
  return { rows: reviewedToAnalysisDetections(reviewDetections), isFallback: false };
}
